import { memo, useCallback, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import ReactModal from 'react-modal';
import styled from '@emotion/styled';

import { themeFont } from '../lib/themeFont';
import { useCurrentRoute } from '../lib/useCurrentRoute';
import useTracking from '../lib/useTracking';
import ArrowLeft from './svgs/ArrowLeft';
import ArrowRight from './svgs/ArrowRight';
import FixedNextImage from './NextImage/FixedNextImage';
import { useRouteBuilder } from '../lib/hooks/useRouteBuilder';
import DynamicNextImage from './NextImage/DynamicNextImage';

const modalStyle = {
  overlay: {
    zIndex: 1100,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
  },
};

/**
 * Keeps the image box matching the photo it shows. Left alone, the image grows
 * to fill the row (`flex: 1`) and keeps whatever width and height next/image
 * gave it (100% for `fill`), so the max constraints clamp the box without
 * keeping the aspect ratio and `object-fit: contain` letterboxes the photo
 * inside an oversized image that covers the overlay around it, so clicking
 * there never reaches the overlay and the lightbox cannot be closed.
 * `!important` because next/image sets those sizes inline.
 */
const FullSizeImage = styled(DynamicNextImage)`
  flex: 0 0 auto;
  max-width: 100vw;
  max-height: 100vh;
  width: auto !important;
  height: auto !important;
`;

const PictureContainer = styled('div')`
  display: block;
  margin: 10px;
  cursor: pointer;
`;

const PrevArrow = styled(ArrowLeft)`
  cursor: pointer;
  fill: white;
  filter: drop-shadow(0 0 5px black);
  height: 75px;
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  width: 75px;
  background-color: rgba(0, 0, 0, 0.1);
  left: 0;
  z-index: 1;
`;

const NextArrow = styled(ArrowRight)`
  cursor: pointer;
  fill: white;
  filter: drop-shadow(0 0 5px black);
  height: 75px;
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  width: 75px;
  background-color: rgba(0, 0, 0, 0.1);
  right: 0;
`;

const StyledModal = styled(ReactModal)`
  border-radius: 0;
  border: none;
  bottom: unset;
  display: flex;
  flex-direction: column;
  left: 50%;
  max-height: 100vh;
  outline: none;
  padding: 0;
  position: fixed;
  right: unset;
  top: 50%;
  transform: translate(-50%, -50%);

  @media (min-width: 768px) {
    svg {
      opacity: 0;
      transition: opacity 0.25s ease-in-out;
    }

    :hover,
    :focus {
      svg {
        opacity: 1;
      }
    }
  }
`;

const Caption = styled('div')`
  background-color: rgba(0, 0, 0, 0.3);
  bottom: 0;
  color: white;
  padding: 8px 16px;
  position: absolute;
  width: 100%;

  ${themeFont('s6')}
`;

export interface PhotoProps {
  id: number;
  alt: string;
  path: string;

  caption?: string | undefined;
  nextPhotoId?: number | undefined;
  prevPhotoId?: number | undefined;
  crop?: string | undefined;
  rotation?: number | null | undefined;
  dimensions?:
    | {
        width: number;
        height: number;
      }
    | undefined;
  thumbnailDimensions?:
    | {
        width: number;
        height: number;
      }
    | undefined;
}

function Photo({
  alt,
  caption,
  nextPhotoId,
  path,
  prevPhotoId,
  id,
  crop,
  rotation = 0,
  dimensions,
  thumbnailDimensions,
}: PhotoProps) {
  const { query, push } = useRouter();

  const currentPage = useCurrentRoute();

  const track = useTracking('photo', 'photos');

  useEffect((): void => {
    const appRoot = document.getElementById('__next');

    if (appRoot) {
      ReactModal.setAppElement('#__next');
    } else {
      ReactModal.setAppElement('body');
    }
  }, []);

  const isModalOpen = useMemo<boolean>(
    () =>
      Array.isArray(query['route']) && typeof query['route'][1] !== 'undefined'
        ? parseInt(query['route'][1], 10) === id
        : false,
    [id, query]
  );

  const { route } = query;

  const formattedRoute = Array.isArray(route) ? route[0] : route;

  const hrefCurrent = useRouteBuilder(`${formattedRoute || 'home'}/${id}`);

  const hrefPrev = useRouteBuilder(`${currentPage}/${prevPhotoId}`);

  const hrefNext = useRouteBuilder(`${currentPage}/${nextPhotoId}`);

  const hrefBack = useRouteBuilder(currentPage);

  const onPictureClick = useCallback((): void => {
    track('open photo modal', 'click photo');
  }, [track]);

  const closeModel = useCallback((): void => {
    push(hrefBack, undefined, {
      shallow: true,
    });
  }, [push, hrefBack]);

  const onRequestClose = useCallback((): void => {
    track('close photo modal', 'click outside modal');

    closeModel();
  }, [closeModel, track]);

  const fullSizePath = path.split('~')[0];
  if (typeof fullSizePath === 'undefined') return <></>;

  /**
   * Check if we're loading a thumbnail
   * If so use smartCrop instead of regular crop with dimensions
   */
  const smartCrop = thumbnailDimensions
    ? `${thumbnailDimensions.width}.${thumbnailDimensions.height}`
    : undefined;

  const height = thumbnailDimensions?.height || 300;
  const width = thumbnailDimensions?.width || 300;

  return (
    <>
      <Link href={hrefCurrent} as={hrefCurrent} shallow>
        <PictureContainer onClick={onPictureClick}>
          <FixedNextImage
            src={fullSizePath}
            crop={!thumbnailDimensions ? crop : undefined}
            smartCrop={thumbnailDimensions ? smartCrop : undefined}
            rotation={rotation}
            alt={alt}
            width={width}
            height={height}
            imageFit="crop"
            style={{ height, width }}
          />
        </PictureContainer>
      </Link>

      <StyledModal
        style={modalStyle}
        isOpen={isModalOpen}
        ariaHideApp={false}
        onRequestClose={onRequestClose}
      >
        {prevPhotoId ? (
          <Link href={hrefPrev} as={hrefPrev} shallow>
            <PrevArrow title="Previous Photo" />
          </Link>
        ) : null}

        <FullSizeImage
          alt={`${alt} - Full Size`}
          src={fullSizePath}
          imageFit="scaleDown"
          width={dimensions?.width}
          height={dimensions?.height}
          crop={crop}
          rotation={rotation}
          quality={90}
        />

        {caption ? <Caption>{caption}</Caption> : null}

        {nextPhotoId ? (
          <Link href={hrefNext} as={hrefNext} shallow>
            <NextArrow title="Next Photo" />
          </Link>
        ) : null}
      </StyledModal>
    </>
  );
}

export default memo(Photo);
